import { CopyOutline18Icon } from "@vygruppen/spor-icon-react";
import {
  getDirection,
  getOffset,
} from "features/CenterContent/RoleContent/VehicleMap/utils";
import LeafletDottedLine from "features/CenterContent/shared/Map/LeafletDottedLine";
import LeafletMarker from "features/CenterContent/shared/Map/LeafletMarker";
import LeafletTooltip from "features/CenterContent/shared/Map/LeafletTooltip";
import { BaseGpsData } from "features/CenterContent/shared/types";
import { handleCopyToClipboard } from "features/CenterContent/shared/utils";
import { useState } from "react";
import { Flex, Text } from "shared/components/Spor";
import { interpolateColors } from "shared/utils/colorUtils";
import { formatDayDateString } from "shared/utils/dateTimeUtils";

export const VehicleMapMarkers = ({ gpsData }: { gpsData: BaseGpsData[] }) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const truncateId = (
    id: string,
    maxLength: number,
    isActive: boolean,
  ): string => {
    if (id.length > maxLength && !isActive) {
      return `${id.slice(0, maxLength)}...`;
    }
    return id;
  };

  const markers = gpsData.map((marker) => {
    const timestamp = formatDayDateString(marker.timestamp);
    const isActive = activeId === marker.id;

    return (
      <LeafletMarker
        key={marker.id}
        position={[marker.gps.latitude, marker.gps.longitude]}
        eventHandlers={{
          click: () => {
            setActiveId((prev) => (prev === marker.id ? null : marker.id));
          },
        }}
      >
        <LeafletTooltip
          permanent
          interactive
          direction={getDirection(marker, marker.closestNeighbor)}
          offset={getOffset(marker, marker.closestNeighbor)}
          eventHandlers={{
            mouseover: () => {
              setActiveId(marker.id);
            },
            mouseout: () => {
              setActiveId(null);
            },
            click: () => {
              setActiveId((prev) => (prev === marker.id ? null : marker.id));
            },
            contextmenu: () => {
              void handleCopyToClipboard(marker.id);
            },
          }}
        >
          <Flex justify="center" alignItems="center" direction="row" gap="6px">
            <Text variant="sm" fontWeight="bold">
              {truncateId(marker.id, 15, isActive)}
            </Text>

            {isActive && (
              <CopyOutline18Icon
                onClick={() => {
                  void handleCopyToClipboard(marker.id);
                }}
                aria-label="Kopier ID"
              />
            )}
          </Flex>

          {isActive && (
            <>
              {marker.phoneNumber && (
                <Text variant="sm" textAlign="center">
                  {marker.phoneNumber}
                </Text>
              )}
              <Text variant="sm" textAlign="center">
                {`${timestamp}` || `Ukjent tidspunkt`}
              </Text>
            </>
          )}
        </LeafletTooltip>
      </LeafletMarker>
    );
  });

  // we only look at the first gpsData since path
  // is only used for mini-gps view (single bus view)
  const path = gpsData[0]?.path ?? [];

  const dottedLines = path.map((location, index) => {
    const timestamp = formatDayDateString(location.timestamp);
    return (
      <LeafletDottedLine
        key={
          location.gps.latitude + location.gps.longitude + location.timestamp
        }
        pathOptions={{
          color: interpolateColors(
            "#00453E", // Spor - DarkTeal
            "#00957A", // Spor - Green Haze
            index / path.length,
          ),
        }}
        center={[location.gps.latitude, location.gps.longitude]}
        radius={3}
      >
        <LeafletTooltip direction="top" offset={[0, -10]}>
          <Text variant="sm" textAlign="center">
            {timestamp || `Ukjent tidspunkt`}
          </Text>
        </LeafletTooltip>
      </LeafletDottedLine>
    );
  });

  return [...markers, ...dottedLines];
};
